import { EmailFolder, isOutlookFiled } from './emailFolder';

/**
 * Search operators for the mail feed's instant (local) search, plus the mapping to the
 * server's Graph $search for deep search.
 *
 *   from:<text> to:<text> subject:<text> has:attachment is:unread is:read is:flagged
 *   in:<inbox|sent|drafts|junk|deleted|archive|filed|<folder name>>
 *   before:<date> after:<date>          (YYYY-MM-DD, or 7d / 2w / 3m / 1y = "that long ago")
 *   quoted values   from:"john smith"   and quoted phrases in free text  "site visit"
 *   negation        -from:noreply  -is:read  -spam
 *
 * Anything that is not a KNOWN operator with a usable value stays plain text, so a URL
 * (https://x), a time (12:30) or `is:whatever` is searched for literally, never eaten. An
 * operator with NO value yet (`from:` while the person is still typing) is ignored rather than
 * turned into "match nothing".
 *
 * A query with no operators, no quotes and no leading '-' parses to exactly the tokens the feed
 * always searched for: lower-cased, split on whitespace, every token must appear somewhere in
 * the row's haystack (matchesSearch builds the same haystack as before).
 *
 * Pure: no React, no Firestore, no clock unless a caller passes `now`.
 */

export type FilterKind = 'from' | 'to' | 'subject' | 'has' | 'is' | 'in' | 'before' | 'after';

export interface Filter {
  kind: FilterKind;
  /** Lower-cased, unquoted value. has: 'attachment'; is: 'unread' | 'read' | 'flagged'. */
  value: string;
  negated: boolean;
  /** before / after only: the resolved cut-off instant (ms since epoch). */
  at?: number;
}

export interface ParsedSearch {
  /** The positive free text, phrases re-quoted - what to send to a text search engine. */
  freeText: string;
  /** Positive free-text terms (lower-case); a quoted phrase is ONE term. */
  terms: string[];
  /** Free-text terms that must NOT appear (from -word / -"a phrase"). */
  excludeTerms: string[];
  filters: Filter[];
  /**
   * True when a known operator with no value (`from:`, `in:`, `from:""`) was left out of
   * the parse. The raw text then says something the parsed query does not, so it must not be
   * handed to a search engine as typed (see toServerQuery).
   */
  ignoredEmptyOperator: boolean;
}

const OPERATORS: ReadonlySet<string> = new Set(['from', 'to', 'subject', 'has', 'is', 'in', 'before', 'after']);
const IS_VALUES: ReadonlySet<string> = new Set(['unread', 'read', 'flagged']);

interface RawToken {
  negated: boolean;
  /** Lower-case operator name when the token was `name:value` with a known name. */
  op?: string;
  value: string;
  /** The raw source text of the token without its leading '-': used to fall back to free text. */
  source: string;
}

const isSpace = (c: string): boolean => /\s/.test(c);

// Phone keyboards (iOS "smart punctuation") turn a typed " into curly quotes, which would make
// from:"john smith" unparseable on exactly the device the owner searches from. So a curly quote
// (or prime / guillemet) counts as a quote - but ONLY where a quote can open (the start of a
// token or of an operator's value) or close a run. Inside a word it is an ordinary character:
// rewriting it there would search for a straight " the row never contains, so the plain query
// 5\u201D nails would stop finding "Order: 5\u201D nails x 200", and a plain query must match
// exactly what it always did.
const QUOTE_CHAR = /[\u201C\u201D\u201E\u201F\u2033\u00AB\u00BB"]/;
const isQuote = (c: string | undefined): boolean => typeof c === 'string' && QUOTE_CHAR.test(c);

/**
 * Read a quoted run starting AT the opening quote. Any kind of quote closes it (a phone may
 * open with one style and close with another). An unclosed quote takes the rest.
 */
function readQuoted(q: string, start: number): { text: string; next: number } {
  for (let k = start + 1; k < q.length; k++) {
    if (isQuote(q[k])) return { text: q.slice(start + 1, k), next: k + 1 };
  }
  return { text: q.slice(start + 1), next: q.length };
}

function tokenize(q: string): RawToken[] {
  const out: RawToken[] = [];
  const n = q.length;
  let i = 0;
  while (i < n) {
    while (i < n && isSpace(q[i])) i++;
    if (i >= n) break;
    let negated = false;
    // A leading '-' negates the NEXT thing. A lone '-' (or one before a space) is just text.
    if (q[i] === '-' && i + 1 < n && !isSpace(q[i + 1])) { negated = true; i++; }

    if (isQuote(q[i])) {
      const { text, next } = readQuoted(q, i);
      out.push({ negated, value: text.trim(), source: text.trim() });
      i = next;
      continue;
    }

    let j = i;
    while (j < n && !isSpace(q[j])) j++;
    const word = q.slice(i, j);
    const colon = word.indexOf(':');
    if (colon > 0) {
      const name = word.slice(0, colon).toLowerCase();
      if (OPERATORS.has(name)) {
        const valueStart = i + colon + 1;
        if (isQuote(q[valueStart])) {
          const { text, next } = readQuoted(q, valueStart);
          out.push({ negated, op: name, value: text.trim(), source: `${word.slice(0, colon + 1)}${text.trim()}` });
          i = next;
          continue;
        }
        out.push({ negated, op: name, value: word.slice(colon + 1), source: word });
        i = j;
        continue;
      }
    }
    out.push({ negated, value: word, source: word });
    i = j;
  }
  return out;
}

/** Start of the given local calendar day, or null when the date does not exist (2024-02-31). */
function calendarDay(y: number, m: number, d: number): number | null {
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) return null;
  return dt.getTime();
}

/**
 * After setMonth / setFullYear, a day that does not exist in the target month has spilled into
 * the next one (31 Mar - 1 month = "31 Feb" = 3 Mar). Step back to the last day of the month we
 * meant, so 1m on 31 May is 30 Apr, not 1 May. 'day' is the date-of-month before the shift; a
 * spill always lands on a DIFFERENT date-of-month (1..day-1), so the comparison is exact.
 */
function clampToMonthEnd(at: Date, day: number): void {
  if (at.getDate() !== day) at.setDate(0);
}

/**
 * before:/after: value -> instant. YYYY-MM-DD (also with / separators) is the start of that
 * local day; Nd / Nw / Nm / Ny is "that long ago" (calendar months and years, clamped to the
 * end of a shorter month so 1m on the 31st does not drift). Null when the value is neither.
 */
export function parseSearchDate(value: string, now: number): number | null {
  const v = value.trim().toLowerCase();
  const abs = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(v);
  if (abs) return calendarDay(Number(abs[1]), Number(abs[2]), Number(abs[3]));
  const rel = /^(\d{1,4})([dwmy])$/.exec(v);
  if (!rel) return null;
  const n = Number(rel[1]);
  const at = new Date(now);
  const day = at.getDate();
  switch (rel[2]) {
    case 'd': at.setDate(day - n); break;
    case 'w': at.setDate(day - n * 7); break;
    case 'm': at.setMonth(at.getMonth() - n); clampToMonthEnd(at, day); break;
    default: at.setFullYear(at.getFullYear() - n); clampToMonthEnd(at, day); break;
  }
  return at.getTime();
}

function toFilter(op: string, rawValue: string, negated: boolean, now: number): Filter | null {
  const value = rawValue.trim().toLowerCase();
  switch (op) {
    case 'from':
    case 'to':
    case 'subject':
    case 'in':
      return { kind: op, value, negated };
    case 'has':
      return value === 'attachment' || value === 'attachments' ? { kind: 'has', value: 'attachment', negated } : null;
    case 'is':
      return IS_VALUES.has(value) ? { kind: 'is', value, negated } : null;
    case 'before':
    case 'after': {
      const at = parseSearchDate(value, now);
      return at === null ? null : { kind: op, value, negated, at };
    }
    default:
      return null;
  }
}

const quotePhrase = (t: string): string => (/\s/.test(t) ? `"${t}"` : t);

/** Parse a search box value. `now` only matters for relative dates (7d, 2w ...). */
export function parseSearchQuery(q: string, now: number = Date.now()): ParsedSearch {
  const terms: string[] = [];
  const excludeTerms: string[] = [];
  const filters: Filter[] = [];
  let ignoredEmptyOperator = false;

  for (const tok of tokenize(typeof q === 'string' ? q : '')) {
    if (tok.op) {
      // `from:` with nothing after it yet: the person is mid-typing, constrain nothing.
      if (!tok.value.trim()) { ignoredEmptyOperator = true; continue; }
      const f = toFilter(tok.op, tok.value, tok.negated, now);
      if (f) { filters.push(f); continue; }
      // Known operator, unusable value (is:foo, before:soon): search for the text as typed.
      const literal = tok.source.trim().toLowerCase();
      if (literal) (tok.negated ? excludeTerms : terms).push(literal);
      continue;
    }
    const term = tok.value.trim().toLowerCase();
    if (!term) continue;
    (tok.negated ? excludeTerms : terms).push(term);
  }

  return { freeText: terms.map(quotePhrase).join(' '), terms, excludeTerms, filters, ignoredEmptyOperator };
}

/** True when the query constrains anything at all (terms, exclusions or operators). */
export function hasSearchConstraints(p: ParsedSearch): boolean {
  return p.terms.length > 0 || p.excludeTerms.length > 0 || p.filters.length > 0;
}

/** True when the query uses any feature beyond plain words (operators, negation). */
export function usesOperators(p: ParsedSearch): boolean {
  return p.filters.length > 0 || p.excludeTerms.length > 0;
}

// ---------------------------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------------------------

/** The fields of a feed row the search reads (a whole FeedEmailData fits). */
export interface SearchableEmail {
  title?: string | null;
  subject?: string | null;
  sender?: string | null;
  message?: string | null;
  to?: string | string[] | null;
  from?: ReadonlyArray<{ address?: string | null; name?: string | null }> | null;
  createdAt?: Date | number | null;
  attachments?: ReadonlyArray<unknown> | null;
  hasAttachments?: boolean | null;
  pinned?: boolean | null;
  outlookFolderName?: string | null;
  outlookFolderPath?: string | null;
}

/** What only the caller knows about a row (its rules live in the feed / emailFolder.ts). */
export interface SearchContext {
  /** normalizeEmailFolder(email). */
  folder: EmailFolder;
  unread: boolean;
  /** Name of the customer the row is matched to, if any (searched as plain text). */
  customerName?: string;
  /** Name of the in-app folder the row is routed to, if any (in:<that name>). */
  virtualFolderName?: string;
}

export interface MatchOptions {
  /**
   * Do not require the positive free-text terms (a server-side hit already matched them).
   * Negated terms (-word) are still applied: the server does not know about them.
   */
  skipTerms?: boolean;
  /** Do not test these filters (the server already applied them). Compared by identity. */
  skipFilters?: ReadonlyArray<Filter>;
}

const lower = (s: unknown): string => (typeof s === 'string' ? s.toLowerCase() : '');

/**
 * The searchable text of a row for plain words. Kept byte-for-byte what the feed's search has
 * always used, so a query without operators matches exactly the same rows as before.
 */
function haystackOf(e: SearchableEmail, customerName: string | undefined): string {
  const to = e.to;
  const toStr = Array.isArray(to) ? to.join(' ') : (typeof to === 'string' ? to : '');
  return [e.title, e.sender, e.message, toStr, customerName].filter(Boolean).join('  ').toLowerCase();
}

function senderText(e: SearchableEmail): string {
  const parts: string[] = [lower(e.sender)];
  for (const f of e.from || []) {
    parts.push(lower(f?.address), lower(f?.name));
  }
  return parts.filter(Boolean).join(' ');
}

function recipientText(e: SearchableEmail): string {
  const to = e.to;
  return lower(Array.isArray(to) ? to.join(' ') : to);
}

function createdAtMs(e: SearchableEmail): number | null {
  const c = e.createdAt;
  if (c instanceof Date) { const t = c.getTime(); return Number.isNaN(t) ? null : t; }
  return typeof c === 'number' && Number.isFinite(c) ? c : null;
}

function folderMatches(value: string, e: SearchableEmail, ctx: SearchContext): boolean {
  switch (value) {
    case 'inbox':
    case 'sent':
    case 'drafts':
    case 'junk':
    case 'deleted':
      return ctx.folder === value;
    // 'archive' is the feed's archive VIEW: Outlook's Archive folder and every other folder the
    // owner filed into. 'filed' narrows that to the other folders only.
    case 'archive':
      return isOutlookFiled(ctx.folder);
    case 'filed':
      return ctx.folder === 'filed';
    default: {
      // A virtual (in-app) folder name, or an Outlook folder name / path.
      const v = lower(ctx.virtualFolderName);
      const name = lower(e.outlookFolderName);
      const path = lower(e.outlookFolderPath);
      return (!!v && v.includes(value)) || (!!name && name.includes(value)) || (!!path && path.includes(value));
    }
  }
}

/** Does the row satisfy this filter AS WRITTEN (ignoring its negation)? */
function filterHolds(f: Filter, e: SearchableEmail, ctx: SearchContext): boolean {
  switch (f.kind) {
    case 'from': return senderText(e).includes(f.value);
    case 'to': return recipientText(e).includes(f.value);
    case 'subject': return (lower(e.title) || lower(e.subject)).includes(f.value);
    case 'has': return (Array.isArray(e.attachments) && e.attachments.length > 0) || e.hasAttachments === true;
    case 'is':
      if (f.value === 'unread') return ctx.unread;
      if (f.value === 'read') return !ctx.unread;
      return e.pinned === true; // flagged
    case 'in': return folderMatches(f.value, e, ctx);
    case 'before': {
      const t = createdAtMs(e);
      return t !== null && f.at !== undefined && t < f.at;
    }
    case 'after': {
      const t = createdAtMs(e);
      return t !== null && f.at !== undefined && t >= f.at;
    }
    default: return false;
  }
}

/**
 * Does this row match the parsed query? A query with nothing in it (e.g. just `from:`) matches
 * nothing, as an empty-token query always did. Otherwise EVERY constraint must hold, except that
 * several positive `in:` filters are alternatives (a message lives in one folder, so ANDing
 * them could never match).
 */
export function matchesSearch(
  email: SearchableEmail,
  parsed: ParsedSearch,
  ctx: SearchContext,
  opts: MatchOptions = {},
): boolean {
  if (!hasSearchConstraints(parsed)) return false;

  const requireTerms = !opts.skipTerms && parsed.terms.length > 0;
  if (requireTerms || parsed.excludeTerms.length > 0) {
    const hay = haystackOf(email, ctx.customerName);
    if (requireTerms) for (const t of parsed.terms) if (!hay.includes(t)) return false;
    for (const t of parsed.excludeTerms) if (hay.includes(t)) return false;
  }

  const skip = opts.skipFilters;
  const active = skip && skip.length > 0 ? parsed.filters.filter((f) => !skip.includes(f)) : parsed.filters;

  const inPositive = active.filter((f) => f.kind === 'in' && !f.negated);
  if (inPositive.length > 0 && !inPositive.some((f) => filterHolds(f, email, ctx))) return false;

  for (const f of active) {
    if (f.kind === 'in' && !f.negated) continue; // handled as a group above
    const holds = filterHolds(f, email, ctx);
    if (f.negated ? holds : !holds) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------------------------
// Deep (server) search
// ---------------------------------------------------------------------------------------------

export interface ServerQuery {
  /** What to send as `query`. Empty = there is nothing the server can search for. */
  query: string;
  /** The filters that are inside `query` (the server applied them; matchesSearch may skip them). */
  handled: Filter[];
}

const kqlValue = (v: string): string => (/\s/.test(v) ? `"${v}"` : v);

/**
 * searchGraphMail (functions/src/functions/http/graph.ts) wraps the query in quotes, escapes any
 * inner quotes and hands it to Graph $search, which reads it as KQL. So `from:`, `to:`,
 * `subject:` and `hasattachments:true` work when sent as-is. Everything else (is:, in:, dates,
 * negations) has no reliable KQL form there and is applied locally to the hits instead.
 *
 * A query with no operators and no negation is sent exactly as typed (today's behaviour) - unless
 * the parser left an empty operator out of it (`invoice in:`, `from: john`): the raw text
 * would then reach Graph as a literal in: / a KQL from-restriction while the local side means plain
 * invoice / john, so the clean form built below is sent instead.
 */
export function toServerQuery(parsed: ParsedSearch, raw: string): ServerQuery {
  if (!usesOperators(parsed) && !parsed.ignoredEmptyOperator) return { query: raw.trim(), handled: [] };

  const parts: string[] = [];
  const handled: Filter[] = [];
  for (const f of parsed.filters) {
    if (f.negated) continue;
    if (f.kind === 'from' || f.kind === 'to' || f.kind === 'subject') {
      parts.push(`${f.kind}:${kqlValue(f.value)}`);
      handled.push(f);
    } else if (f.kind === 'has') {
      parts.push('hasattachments:true');
      handled.push(f);
    }
  }
  if (parsed.freeText) parts.push(parsed.freeText);
  return { query: parts.join(' ').trim(), handled };
}

// ---------------------------------------------------------------------------------------------
// Help + small string helpers
// ---------------------------------------------------------------------------------------------

export interface OperatorHelp {
  /** Shown as the example. */
  example: string;
  /** What tapping the entry puts in the search box (a stem when a value is needed). */
  insert: string;
  note: string;
}

export const SEARCH_OPERATOR_HELP: ReadonlyArray<OperatorHelp> = [
  { example: 'from:name', insert: 'from:', note: 'sender name or address' },
  { example: 'to:name', insert: 'to:', note: 'recipient' },
  { example: 'subject:invoice', insert: 'subject:', note: 'in the subject' },
  { example: 'has:attachment', insert: 'has:attachment', note: 'with a file' },
  { example: 'is:unread', insert: 'is:unread', note: 'also is:read, is:flagged' },
  { example: 'in:deleted', insert: 'in:', note: 'inbox sent drafts junk deleted archive or a folder name' },
  { example: 'after:2w', insert: 'after:', note: 'newer than 2 weeks (7d 3m 1y, or 2025-06-30)' },
  { example: 'before:2025-06-30', insert: 'before:', note: 'older than a date' },
  { example: 'from:"john smith"', insert: 'from:"', note: 'quote values with spaces' },
  { example: '-from:noreply', insert: '-', note: 'a leading - excludes' },
];

/** Add `snippet` to the end of a query with one space between, never doubling spaces. */
export function appendToQuery(current: string, snippet: string): string {
  const base = (current || '').replace(/\s+$/, '');
  return base ? `${base} ${snippet}` : snippet;
}

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * One case-insensitive regex with a SINGLE capture group over all the terms (longest first so
 * "invoice" wins over "in"), or null when there is nothing to highlight. With one capture group,
 * String.split puts every match at an odd index - callers need no stateful regex.test.
 */
export function highlightRegex(terms: ReadonlyArray<string>): RegExp | null {
  const uniq = Array.from(new Set(terms.filter(Boolean)));
  if (uniq.length === 0) return null;
  uniq.sort((a, b) => b.length - a.length);
  return new RegExp(`(${uniq.map(escapeRegExp).join('|')})`, 'gi');
}
